/**
 * Service global pour coordonner les transitions d'application critiques
 * Évite les conflits entre logout, version refresh, et boot recovery
 * 
 * @module AppTransitionState
 */

export type TransitionState = 'idle' | 'logging_out' | 'version_refresh' | 'boot_recovery';

// Singleton state
let currentTransition: TransitionState = 'idle';
const listeners: Set<(state: TransitionState) => void> = new Set();

/**
 * AppTransitionState - Singleton pour gérer les transitions globales
 * Garantit qu'une seule transition critique se produit à la fois
 */
export const appTransitionState = {
  /**
   * Obtient l'état de transition actuel
   */
  getState(): TransitionState {
    return currentTransition;
  },

  /**
   * Définit l'état de transition (si pas déjà occupé par une autre transition)
   * @returns true si la transition a été acceptée, false sinon
   */
  setState(state: TransitionState): boolean {
    // Si on essaie de définir une transition alors qu'une autre est en cours, refuser
    if (state !== 'idle' && currentTransition !== 'idle') {
      console.log(`[AppTransitionState] ⚠️ Transition "${state}" refusée, "${currentTransition}" en cours`);
      return false;
    }

    console.log(`[AppTransitionState] 🔄 Transition: ${currentTransition} → ${state}`);
    currentTransition = state;
    
    // Notifier les listeners
    listeners.forEach(listener => {
      try {
        listener(state);
      } catch (e) {
        console.error('[AppTransitionState] Listener error:', e);
      }
    });

    return true;
  },

  /**
   * Force l'état de transition (ignore les conflits)
   * À utiliser uniquement pour les resets critiques
   */
  forceState(state: TransitionState): void {
    console.log(`[AppTransitionState] ⚡ Force transition: ${currentTransition} → ${state}`);
    currentTransition = state;
    listeners.forEach(listener => {
      try {
        listener(state);
      } catch (e) {
        console.error('[AppTransitionState] Listener error:', e);
      }
    });
  },

  /**
   * Vérifie si une transition est active
   */
  isTransitionActive(): boolean {
    return currentTransition !== 'idle';
  },

  /**
   * Vérifie si le logout est en cours
   */
  isLoggingOut(): boolean {
    return currentTransition === 'logging_out';
  },

  /**
   * Vérifie si un refresh de version est en cours
   */
  isVersionRefresh(): boolean {
    return currentTransition === 'version_refresh';
  },

  /**
   * Vérifie si un boot recovery est en cours
   */
  isBootRecovery(): boolean {
    return currentTransition === 'boot_recovery';
  },

  /**
   * Reset à idle (fin de transition)
   */
  reset(): void {
    this.forceState('idle');
  },

  /**
   * Ajoute un listener pour les changements d'état
   */
  subscribe(listener: (state: TransitionState) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
};
